import { useEffect, useState, useRef, use } from 'react'
import Image from 'next/image'
import { useTranslation } from 'src/utils/i18n'
import { useDispatch } from 'react-redux'
import { setCurrentTab } from 'src/store/apps/Common'
import dayjs from 'dayjs'
import { DatePicker } from 'antd'
import { useAndroidDevicePagedData } from 'src/hooks/useAndroidDevicePagedData'
import { buildTimeParams, toMillis } from 'src/utils/dateRangeParams'
import ContentLoading from 'src/components/ContentLoading'

const Notifications = () => {
  const { t, locale } = useTranslation('common')
  const dispatch = useDispatch()
  const [showData, setShowData] = useState([]) //展示数据
  const [showDate, setShowDate] = useState(false) //展示选择日期框
  const [selectDate, setSelectDate] = useState(dayjs().format('YYYY-MM-DD')) //选择的日期
  const parentRef = useRef(null)
  const { context, data: allData, loading: isLoading, loadNext, setDateRange } = useAndroidDevicePagedData({
    source: 'local',
    category: 'NOTIFICATION',
    limit: 100
  })

  useEffect(() => {
    setShowData([])
    setShowDate(false)
    setSelectDate(dayjs().format('YYYY-MM-DD'))
  }, [context?.requestKey])

  // 通知按「选中日期」走接口筛选（下面的本地过滤作为兜底）
  useEffect(() => {
    if (!selectDate) return

    const { startTime, endTime } = buildTimeParams([selectDate, selectDate])
    setDateRange({ startTime, endTime })
  }, [selectDate, context?.requestKey, setDateRange])

  const dom = useRef(null)

  // 滚动百分比
  const [scroll, setScroll] = useState(0)

  // 获取滚动百分比
  useEffect(() => {
    const handScroll = () => {
      if (dom.current) {
        const { scrollTop, scrollHeight, clientHeight } = dom.current
        const progress = (scrollTop / (scrollHeight - clientHeight)) * 100
        setScroll(progress.toFixed(0))
      }
    }
    const currentRef = dom.current
    if (currentRef) {
      currentRef.addEventListener('scroll', handScroll)
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handScroll)
      }
    }
  }, [])

  useEffect(() => {
    // 滚动触发加载
    if (scroll >= 100 && !isLoading) loadNext()
  }, [isLoading, loadNext, scroll])

  useEffect(() => {
    // 初始加载（showData为空时）
    if (showData.length === 0 && allData.length > 0 && !isLoading) loadNext()
  }, [allData.length, isLoading, loadNext, showData.length])

  useEffect(() => {
    // 计算当天起始时间和结束时间（毫秒）
    const start = dayjs(`${selectDate} 00:00:00`).valueOf()
    const end = dayjs(`${selectDate} 23:59:59`).valueOf()
    // item.time 可能是秒/毫秒时间戳或日期字符串，统一成毫秒再比较
    const newData = allData.filter(item => {
      const itemTime = toMillis(item.time)

      return itemTime >= start && itemTime <= end
    })
    setShowData(newData)
  }, [selectDate, allData])

  // 获取当前语种的dayjs
  useEffect(() => {
    const handleLocale = async () => {
      switch (locale) {
        case 'fr':
          await import('dayjs/locale/fr')
          dayjs.locale('fr')
          break
        case 'jp':
          await import('dayjs/locale/ja')
          dayjs.locale('ja')
          break
        case 'es':
          await import('dayjs/locale/es')
          dayjs.locale('es')
          break
        case 'de':
          await import('dayjs/locale/de')
          dayjs.locale('de')
          break
        case 'it-ch':
          await import('dayjs/locale/it')
          dayjs.locale('it')
          break
        case 'pt':
          await import('dayjs/locale/pt')
          dayjs.locale('pt')
          break
        case 'kr':
          await import('dayjs/locale/ko')
          dayjs.locale('ko')
          break
        case 'ar':
          await import('dayjs/locale/ar')
          dayjs.locale('ar')
          break
        case 'tr':
          await import('dayjs/locale/tr')
          dayjs.locale('tr')
          break
        default:
          await import('dayjs/locale/en')
          dayjs.locale('en')
      }
    }

    handleLocale()
  }, [locale])

  useEffect(() => {
    const title = t('Notifications')
    dispatch(setCurrentTab(title))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, t])

  const button = () => (
    <div className='w-[337px] smj:w-full smj:px-2 h-[44px] smjs:my-[15px] smj:mt-5 flex items-center smjs:mr-[58px]'>
      <Image src={'/image/arrow.svg'} width={24} height={24} alt='left' className='cursor-pointer' onClick={() => handleChangeDay('reduce')} />
      <div className='w-[257px] h-[44px] smj:w-[calc(100%-68px)] mx-4 smj:mx-[10px] bg-[#F2F3F5] rounded-[8px] relative cursor-pointer'
        ref={parentRef}
        onClick={e => {
          e.stopPropagation()
          setShowDate(!showDate)
        }}>
        <Image src={'/image/calendar.svg'} width={24} height={24} alt='calendar' className='absolute top-[10px] left-3' />
        <p className='text-center text-[14px] leading-[22px] my-[11px] text-[#484B60]'>{selectDate}</p>
        {showDate && (
          <DatePicker
            open={showDate}
            onOpenChange={setShowDate}
            value={selectDate ? dayjs(selectDate) : null}
            format="YYYY-MM-DD"
            getPopupContainer={() => parentRef.current}
            className='absolute w-full h-full top-0 left-0 right-0 opacity-0 cursor-pointer'
            onChange={(date, dateString) => {
              setSelectDate(dateString)
              setShowDate(false)
            }}
          />
        )}
      </div>
      <Image src={'/image/arrow.svg'} width={24} height={24} alt='right' className='cursor-pointer rotate-180' onClick={() => handleChangeDay('add')} />
    </div>
  )

  const handleChangeDay = (type) => {
    let newDate
    if (type == 'add') newDate = dayjs(selectDate).add(1, 'day').format('YYYY-MM-DD')
    else if (type == 'reduce') newDate = dayjs(selectDate).subtract(1, 'day').format('YYYY-MM-DD')
    setSelectDate(newDate)
  }

  return (
    <div className='w-full bg-white !h-[calc(var(--vh2)*100-215px)] smj:!h-[calc(var(--vh2)*100-90px)] [box-shadow:0px_0px_12px_0px_#00000021] rounded-[10px] overflow-hidden relative'>
      <div className='w-full h-[75px] smj:h-auto flex smj:flex-col smjs:justify-between smjs:border-b-[1px] smjs:border-[#E2E2E4]'>
        <div className='pl-[32px] smj:p-5 py-[22px] text-[20px] leading-[30px] smj:text-[18px] smj:leading-[26px] font-[600] text-[#282938] smj:border-b-[1px] smj:border-[#E2E2E4]'>
          {t('Timeline View')}
        </div>
        {button()}
      </div>
      <div className='w-full !h-[calc(var(--vh2)*100-290px)] smj:!h-[calc(var(--vh2)*100-218px)] overflow-auto p-[32px] smj:px-[10px] smj:py-4 smj:mt-2' ref={dom}>
        {showData.map((item, i) => {
          return (
            <div key={i} className='w-full h-auto flex'>
              <div className='!w-[42px] flex-shrink-0 text-[14px] leading-[22px] text-[#2D3045] smj:hidden pt-1'>{dayjs(item.time).format('HH:mm')}</div>
              <div className='flex flex-col flex-shrink-0 items-center smjs:pl-6 pr-[18px] smj:pr-3'>
                <Image src={'https://cos.spyx.com/' + item?.iconUrl} width={42} height={42} alt='icon' className='w-[42px] h-[42px] smj:w-[36px] smj:h-[36px]' />
                {(i !== showData.length - 1) && <div className='border-l border-dashed border-[#BEC2D0] h-[calc(100%-42px)] smj:h-[calc(100%-36px)]'></div>}
              </div>
              <div className={`flex flex-col pb-[32px] smj:pb-[24px] w-[-webkit-fill-available] ${i == showData.length - 1 && '!pb-0'}`}>
                <div className='flex items-center justify-between pt-1'>
                  <div className='text-[16px] leading-[24px] font-[600] text-[#1B1C2B]'>{item?.app}</div>
                  <div className='text-[14px] leading-[24px] text-[#787A87] smjs:hidden'>{dayjs(item.time).format('HH:mm')}</div>
                </div>
                <div className='mt-2 text-[14px] leading-[22px] text-[#2D3045]'>{item?.title}</div>
                <div className='mt-1 text-[14px] leading-[22px] text-[#2D3045]'>{item?.text}</div>
              </div>
            </div>
          )
        })}
      </div>

      {/* 没数据提示 */}
      {isLoading ? (
        <div className='absolute inset-0 z-[20] bg-[#FFF] rounded-[10px]'>
          <ContentLoading />
        </div>
      ) : null}
{(allData.length > 0 || isLoading) ||
        <div className='text-center px-5 absolute inset-0 flex justify-center items-center pointer-events-none'>
          {t('It appears theres no data to show here yet')}<br />
          {t('If data exists it may take up to 1 hour to download and appear')}<br />
          {t('We suggest checking back in a little while')}
        </div>}
    </div>
  )
}

export default Notifications
